import { useEffect, useMemo, useState } from 'react'
import { useReportBuilderStore } from '../stores/reportBuilderStore'

const V3_GROUPABLE_COMPONENT_TYPES = [
	'rating',
	'select',
	'number',
	'9boxrating',
	'text',
	'textarea',
	'date',
	'checkbox',
	'radio',
	'multiselect',
	'radio-group',
]

export function CategoryManager() {
	const {
		currentConfig,
		componentLabels,
		isLoadingComponentLabels,
		availableGroupCategories,
		loadComponentLabels,
		setComponentLabel,
		deleteComponentLabel,
		loadGroupCategories,
	} = useReportBuilderStore()

	const [editingComponent, setEditingComponent] = useState<string | null>(null)
	const [newCategory, setNewCategory] = useState('')
	const [searchTerm, setSearchTerm] = useState('')

	useEffect(() => {
		if (currentConfig.selectedFormIds.length > 0) {
			loadComponentLabels(currentConfig.selectedFormIds)
			loadGroupCategories()
		}
	}, [currentConfig.selectedFormIds, loadComponentLabels, loadGroupCategories])

	const groupableComponents = useMemo(() => {
		return componentLabels.filter((c) => V3_GROUPABLE_COMPONENT_TYPES.includes(c.componentName))
	}, [componentLabels])

	const filteredComponents = useMemo(() => {
		if (!searchTerm) return groupableComponents
		const lowerSearch = searchTerm.toLowerCase()
		return groupableComponents.filter(
			(c) =>
				c.componentLabel.toLowerCase().includes(lowerSearch) ||
				c.formName.toLowerCase().includes(lowerSearch) ||
				c.groupCategory?.toLowerCase().includes(lowerSearch),
		)
	}, [groupableComponents, searchTerm])

	const categoryCounts = useMemo(() => {
		const counts: Record<string, number> = {}
		for (const c of groupableComponents) {
			if (c.groupCategory) {
				counts[c.groupCategory] = (counts[c.groupCategory] || 0) + 1
			}
		}
		return counts
	}, [groupableComponents])

	const startEdit = (componentId: string, currentCategory: string | null) => {
		setEditingComponent(componentId)
		setNewCategory(currentCategory || '')
	}

	const saveCategory = async (componentId: string) => {
		if (newCategory.trim()) {
			await setComponentLabel(componentId, newCategory.trim())
		} else {
			await deleteComponentLabel(componentId)
		}
		setEditingComponent(null)
		setNewCategory('')
	}

	const cancelEdit = () => {
		setEditingComponent(null)
		setNewCategory('')
	}

	const handleKeyDown = (e: React.KeyboardEvent, componentId: string) => {
		if (e.key === 'Enter') {
			saveCategory(componentId)
		} else if (e.key === 'Escape') {
			cancelEdit()
		}
	}

	if (currentConfig.selectedFormIds.length === 0) {
		return (
			<div className='p-4 bg-neutral-50 rounded-lg text-center'>
				<p className='text-sm text-neutral-500'>
					Please select at least one form in Data Selection to manage component categories.
				</p>
			</div>
		)
	}

	return (
		<div className='space-y-4'>
			<div>
				<h3 className='text-sm font-medium text-neutral-700 mb-2'>Component Group Categories</h3>
				<p className='text-xs text-neutral-500 mb-3'>
					Assign group categories to form components for organizing reports. Components with the same category will be
					grouped together.
				</p>
			</div>

			{availableGroupCategories.length > 0 && (
				<div className='flex flex-wrap gap-2 mb-3'>
					{availableGroupCategories.map((cat) => (
						<span
							key={cat}
							className='inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full'>
							{cat}
							<span className='text-blue-500'>({categoryCounts[cat] || 0})</span>
						</span>
					))}
				</div>
			)}

			<div className='relative'>
				<input
					type='text'
					placeholder='Search components...'
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className='w-full px-3 py-2 pl-9 border border-neutral-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
				/>
				<svg
					className='absolute left-3 top-2.5 h-4 w-4 text-neutral-400'
					fill='none'
					viewBox='0 0 24 24'
					stroke='currentColor'
					aria-hidden='true'>
					<path
						strokeLinecap='round'
						strokeLinejoin='round'
						strokeWidth={2}
						d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
					/>
				</svg>
			</div>

			{isLoadingComponentLabels ? (
				<div className='flex items-center justify-center p-8'>
					<svg className='animate-spin h-6 w-6 text-blue-600' viewBox='0 0 24 24' aria-hidden='true'>
						<circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' fill='none' />
						<path
							className='opacity-75'
							fill='currentColor'
							d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
						/>
					</svg>
					<span className='ml-2 text-sm text-neutral-600'>Loading components...</span>
				</div>
			) : filteredComponents.length === 0 ? (
				<p className='text-sm text-neutral-500 p-4 bg-neutral-50 rounded-lg text-center'>
					{searchTerm ? 'No components match your search.' : 'No groupable components found in the selected forms.'}
				</p>
			) : (
				<ul className='space-y-2 max-h-[400px] overflow-y-auto'>
					{filteredComponents.map((component) => (
						<li
							key={component.componentId}
							className='flex items-center justify-between p-3 border border-neutral-200 rounded-md bg-white hover:bg-neutral-50'>
							{editingComponent === component.componentId ? (
								<div className='flex items-center gap-2 flex-1'>
									<input
										type='text'
										value={newCategory}
										onChange={(e) => setNewCategory(e.target.value)}
										onKeyDown={(e) => handleKeyDown(e, component.componentId)}
										placeholder='Enter category name...'
										list='category-suggestions'
										className='flex-1 px-2 py-1 border border-neutral-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500'
									/>
									<datalist id='category-suggestions'>
										{availableGroupCategories.map((cat) => (
											<option key={cat} value={cat} />
										))}
									</datalist>
									<button
										type='button'
										onClick={() => saveCategory(component.componentId)}
										className='px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700'>
										Save
									</button>
									<button
										type='button'
										onClick={cancelEdit}
										className='px-3 py-1 border border-neutral-300 text-neutral-600 rounded text-sm hover:bg-neutral-100'>
										Cancel
									</button>
								</div>
							) : (
								<>
									<div className='flex-1'>
										<div className='flex items-center gap-2'>
											<span className='text-sm font-medium text-neutral-900'>{component.componentLabel}</span>
											{component.groupCategory && (
												<span className='text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full'>
													{component.groupCategory}
												</span>
											)}
										</div>
										<div className='text-xs text-neutral-500 mt-0.5'>
											<span className='inline-flex items-center gap-1'>
												<span className='px-1.5 py-0.5 bg-neutral-100 text-neutral-600 rounded text-[10px] uppercase font-medium'>
													{component.componentName}
												</span>
												<span>Form: {component.formName}</span>
											</span>
										</div>
									</div>
									<div className='flex items-center gap-1'>
										<button
											type='button'
											onClick={() => startEdit(component.componentId, component.groupCategory)}
											className='text-neutral-400 hover:text-blue-500 transition-colors p-1.5 rounded hover:bg-blue-50'
											title={component.groupCategory ? 'Edit category' : 'Set category'}>
											<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
												<path
													strokeLinecap='round'
													strokeLinejoin='round'
													strokeWidth={2}
													d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
												/>
											</svg>
										</button>
										{component.groupCategory && (
											<button
												type='button'
												onClick={() => deleteComponentLabel(component.componentId)}
												className='text-neutral-400 hover:text-red-500 transition-colors p-1.5 rounded hover:bg-red-50'
												title='Remove category'>
												<svg
													className='h-4 w-4'
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'
													aria-hidden='true'>
													<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
												</svg>
											</button>
										)}
									</div>
								</>
							)}
						</li>
					))}
				</ul>
			)}
		</div>
	)
}
